/* eslint-disable */
import { useState, useMemo, useEffect } from "react";
import {
  Card,
  Text,
  Select,
  Table,
  ScrollArea,
  Badge,
  TextInput,
  Pagination,
  createStyles,
  Center,
  Flex,
  Loader,
} from "@mantine/core";
import { IconSearch, IconPencil, IconTrash } from "@tabler/icons-react";
import EditDepartment from "../editDepartment";
import DeleteDepartment from "../deleteModal";
import { useGetDepartments } from "api/department/getAllDepartment";
import { PermissionActionIcon } from "api/permission/permissionActionBtn";
import { useUser } from "hooks/useUser";

const useStyles = createStyles((theme) => ({
  containerCard: {
    padding: "18px",
    borderRadius: theme.radius.md,
    boxShadow: "none",
    border: `1px solid ${theme.colors.gray ? theme.colors.gray[2] : "#e9ecef"}`,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  titleLeft: {
    fontWeight: 600,
    color: theme.colors.dark ? theme.colors.dark[6] : "#222",
  },
  controls: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  perPageSelect: {
    width: "110px",
  },
  tableHeader: {
    backgroundColor:
      theme.colorScheme === "light" ? "#f5f5f5" : theme.colors.dark[7],
    fontWeight: 700,
    fontSize: "13px",
    color: theme.colors.gray ? theme.colors.gray[7] : "#666",
  },
  rowCell: {
    paddingTop: "18px",
    paddingBottom: "18px",
    verticalAlign: "top",
  },
  iconWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
    whiteSpace: "nowrap",
  },
  actionsCell: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
  },
  smallBadge: {
    borderRadius: "6px",
    padding: "4px 8px",
    fontSize: "11px",
    height: "auto",
  },
  footerText: {
    fontSize: "13px",
    color: theme.colors.gray ? theme.colors.gray[6] : "#6b7280",
  },
  handbookLink: {
    display: "inline-block",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  descCell: {
    maxWidth: 320,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  },
  logoCell: {
    textAlign: "center",
  },
  departmentNameCol: {
    width: 180,      // try 220–280
    minWidth: 180,
  },
  actionGroup: {
    display: "flex",
    gap: 6,
    padding: "4px",
    border: "1px solid #d1d5db", // gray border
    borderRadius: 6,
    alignItems: "center",
  },
  actionIconBox: {
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  
    "&:hover": {
      backgroundColor: "#f9fafb",
      borderColor: "#9ca3af",
    },
  },
  
  
  
}));

type Member = { _id: string; email?: string };

type DeptFromApi = {
  _id: string;
  logo?: string;
  name: string;
  description?: string;
  handbook?: string;
  status: "active" | "inactive" | string;
  organization?: string;
  members: Member[];
  createdAt?: string;
  updatedAt?: string;
};

export default function DepartmentTable() {
  const { classes } = useStyles();
  const { user } = useUser();
  const [opened, setOpened] = useState(false);
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [perPage, setPerPage] = useState("10");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Check if user is a member or supervisor
  const roleNormalized = (user?.role ?? "").toString();
  const isMember = /member/i.test(roleNormalized);
  const isSupervisor = /supervisor/i.test(roleNormalized);

  // Use the hook with pagination parameters
  const { data, isLoading } = useGetDepartments(page, parseInt(perPage));

  const departments = useMemo(() => {
    let depts = data?.data ?? [];

    // If user is a member or supervisor, only show their assigned department
    if ((isMember || isSupervisor) && user?.department?._id) {
      depts = depts.filter((d) => {
        return d._id?.toString() === user.department?._id?.toString();
      });
    }

    return depts;
  }, [data, isMember, isSupervisor, user?.department?._id]);

  const isRestrictedRole = (isMember || isSupervisor) && user?.department?._id;
  const totalEntries = isRestrictedRole
    ? (departments.length > 0 ? 1 : 0)
    : (data?.pagination?.total ?? 0);
  const pageCount = isRestrictedRole
    ? (departments.length > 0 ? 1 : 0)
    : (data?.pagination?.pages ?? 1);

  // Filter departments on client-side for search functionality
  const filtered = useMemo(() => {
    if (!search.trim()) return departments;
    
    const q = search.toLowerCase();
    return departments.filter((d) => {
      if ((d.name || "").toLowerCase().includes(q)) return true;
      if ((d.description || "").toLowerCase().includes(q)) return true;
      if ((d.handbook || "").toLowerCase().includes(q)) return true;
      if (Array.isArray(d.members)) {
        return d.members.some((m:any) => (m.email || "").toLowerCase().includes(q));
      }
      return false;
    });
  }, [departments, search]);

  // Reset to page 1 when filters change (except page changes)
  useEffect(() => {
    setPage(1);
  }, [statusFilter, perPage]);

  const display = useMemo(() => {
    if (search.trim()) {
      const start = (page - 1) * parseInt(perPage);
      return filtered.slice(start, start + parseInt(perPage));
    }
    return departments;
  }, [departments, filtered, page, perPage, search]);

  const currentTotal = search.trim() ? filtered.length : totalEntries;
  const currentPageCount = search.trim() 
    ? Math.max(1, Math.ceil(filtered.length / parseInt(perPage)))
    : pageCount;

  return (
    <Card className={classes.containerCard}>
      <div className={classes.headerRow}>
        <div>
          <Text className={classes.titleLeft} size="lg">
            Department List
          </Text>
          <Text size="xs" color="dimmed" mt={4}>
            Total: {currentTotal} departments
          </Text>
        </div>

        <div className={classes.controls}>
          <Select
            label="Status"
            data={["All", "Active", "Inactive"]}
            value={statusFilter ?? "All"}
            onChange={(v) => setStatusFilter(v === "All" ? null : v)}
            placeholder="Status"
            sx={{ width: "120px" }}
            withinPortal
          />
          
          <Select
            label="Rows"
            data={["10", "20", "30", "50", "100"]}
            value={perPage}
            onChange={(value) => {
              setPerPage(value || "10");
              setPage(1);
            }}
            className={classes.perPageSelect}
            withinPortal
          />
          
          <TextInput
            label="Search"
            icon={<IconSearch size={16} />}
            placeholder="Search departments..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            sx={{ width: "220px" }}
          />
        </div>
      </div>

      <ScrollArea>
        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : (
          <Table verticalSpacing="sm" highlightOnHover style={{ minHeight: 200 }}>
            <thead style={{ backgroundColor: "#f5f5f5" }}>
              <tr>
                <th className={classes.tableHeader} style={{ textAlign: "center" }}>
                  Logo
                </th>
                <th className={`${classes.tableHeader} ${classes.departmentNameCol}`}>
                Department Name</th>
                <th className={classes.tableHeader} style={{ textAlign: "center" }}>
                Your Role</th>
                <th className={classes.tableHeader}>Description</th>
                <th className={classes.tableHeader}>Handbook</th>
                <th
                  className={classes.tableHeader}
                  style={{ textAlign: "center" }}
                >
                  Employees
                </th>
                <th
                  className={classes.tableHeader}
                  style={{ textAlign: "center" }}
                >
                  Status
                </th>
                <th
                  className={classes.tableHeader}
                  style={{ textAlign: "center" }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {display.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: "40px" }}>
                    <Text size="sm" color="dimmed">
                      {search.trim() ? "No departments match your search" : "No departments found"}
                    </Text>
                  </td>
                </tr>
              ) : (
                display.map((dept) => (
                  <tr key={dept._id}>
                    <td className={`${classes.rowCell} ${classes.logoCell}`}>
                      {dept?.logo ? (
                        <img
                          src={dept.logo}
                          alt="logo"
                          style={{
                            width: 35,
                            height: 35,
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "1px solid #ddd",
                            margin: "0 auto",
                          }}
                        />
                      ) : (
                        <Text fw={500} size="sm" color="dimmed">
                          N/A
                        </Text>
                      )}
                    </td>

                    <td className={classes.rowCell}>
                      <Text fw={500} size="sm">
                        {dept.name}
                      </Text>
                    </td>

                    <td className={classes.rowCell} style={{ textAlign: "center" }}>
                      <Badge
                        color={dept.userRole === "director" ? "blue" : "gray"}
                        variant="light"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {dept.userRole || "member"}
                      </Badge>
                    </td>

                    <td className={classes.rowCell}>
                      <Text
                        size="sm"
                        className={classes.descCell}
                        title={dept.description ?? ""}
                      >
                        {dept.description ?? "-"}
                      </Text>
                    </td>

                    <td className={classes.rowCell}>
                      {dept.handbook ? (
                        <Text
                          size="sm"
                          component="a"
                          href={dept.handbook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.handbookLink}
                          title={dept.handbook}
                        >
                          {dept.handbook}
                        </Text>
                      ) : (
                        <Text size="sm" color="dimmed">
                          -
                        </Text>
                      )}
                    </td>

                    <td className={classes.rowCell} style={{ textAlign: "center" }}>
                      <Text size="sm">
                        {String(dept.members?.length ?? 0).padStart(2, "0")}
                      </Text>
                    </td>

                    <td className={classes.rowCell} style={{ textAlign: "center" }}>
                    <Badge color="green" variant="light">
                        {dept.status
                          ? String(dept.status).charAt(0).toUpperCase() +
                            String(dept.status).slice(1)
                          : "-"}
                      </Badge>
                    </td>

                    <td className={classes.rowCell}>
  <div className={classes.actionsCell}>
    <PermissionActionIcon
      permission="department.update"
      variant="transparent"
      onClick={() => {
        setDepartmentId(dept._id);
        setOpened(true);
      }}
      className={classes.actionIconBox}
    >
      <IconPencil size={16} />
    </PermissionActionIcon>

    <PermissionActionIcon
      permission="department.delete"
      variant="transparent"
      onClick={() => {
        setDepartmentId(dept._id);
        setOpenedDelete(true);
      }}
      className={classes.actionIconBox}
    >
      <IconTrash size={16} />
    </PermissionActionIcon>
  </div>
</td>

                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </ScrollArea>

      <Flex justify="space-between" align="center" mt="md">
        <Text className={classes.footerText}>
          Showing {Math.min(1 + (page - 1) * parseInt(perPage), currentTotal)} -{" "}
          {Math.min(page * parseInt(perPage), currentTotal)} of {currentTotal}{" "}
          entries
          {search.trim() && ` (${filtered.length} filtered)`}
        </Text>

        <Center>
          <Pagination
            page={page}
            onChange={setPage}
            total={currentPageCount}
            size="sm"
            siblings={1}
            boundaries={1}
            disabled={isLoading}
          />
        </Center>
      </Flex>
      
      <EditDepartment
        departmentId={departmentId}
        opened={opened}
        onClose={() => setOpened(false)}
      />
      <DeleteDepartment
        opened={openedDelete}
        departmentId={departmentId}
        onClose={() => setOpenedDelete(false)}
      />
    </Card>
  );
}